<script setup>
import { ref, watch, provide, computed, inject } from 'vue';
import { useIsBreakpointSize } from '../../hooks/useIsMobile';
import BaseModal from '../BaseModal/BaseModal.vue';
import InputField from '../InputField/InputField.vue';

const isOpen = ref(false);
const promoCodeResponse = ref(false);
const displayBtn = ref(true);
const error = ref('');
const modal = ref(null);
const isMobile = useIsBreakpointSize(991);

provide('injectedIsOpen', isOpen);

const openModal = (e) => {
  isOpen.value = true;
};

const closeModal = (e) => {
  isOpen.value = false;
  if (promoCodeResponse.value === false && isOpen.value === false || !inputValueRef.value) {
    displayBtn.value = true;
  } else {
    displayBtn.value = false;
  }
};

const focusPromoInput = () => {
  setTimeout(() => {
    const activePromoInput = document.querySelector('.display-promo--active .display-promo__input');
    activePromoInput.focus();
  }, 450);
}

const setIsOpen = (value) => {
  isOpen.value = value;
};

const setDisplayButton = (value) => {
  displayBtn.value = value;
};

const props = defineProps({
  inputValue: {
    type: String,
    default: '',
    required: false,
  }
});

const cmsProps = inject('cms-props');

const emit = defineEmits([
  'update:inputValue',
  'update:hasFocus',
  'update:blurred',
]);

const emitHandler = (name, value) => {
  emit(name, value);
};

const inputValueRef = ref(props.inputValue  ? props.inputValue : '');
const child = ref(null);

const updateInput = (value) => {
  inputValueRef.value = value;
  emitHandler('update:inputValue', value);
};

const focusHandler = (e) => {
  emitHandler('update:hasFocus', e);
};

const blurHandler = (e) => {
  emitHandler('update:blurred', e);
  // set the value and update state on blur
  promoEnterHandler();
};

const clearInput = () => {
  child.value?.clearInput();
};

const resetPromo = () => {
  setIsOpen(false);
  setPromoCode(false);
  setDisplayButton(true);
  clearInput();
  inputValueRef.value = '';
};

const setPromoCode = (e) => {
  promoCodeResponse.value = true;
  //Request to add promo code to Amadeus
};

const promoEnterHandler = () => {
  if(inputValueRef.value) {
    inputValueRef.value && setPromoCode();
    setDisplayButton(false);
    emitHandler('update:inputValue', inputValueRef.value);
  } else {
    resetPromo();
  }
  setIsOpen(false);
};

defineExpose({
  clearInput: clearInput,
});

const childEmits = (value) => {
  if (value === 'isClosed') {
    closeModal();
  }
};

const addPromoButtonHandler = () => {
  if(isMobile.value) {
    setIsOpen(true);
  } else {
    setDisplayButton(false);
    setIsOpen(true);
    focusPromoInput();
  }
}

const isDisabled = computed(() => {
  return !inputValueRef.value.length > 0;
});

watch(() => props.inputValue, (newValue) => {
  if (newValue !== inputValueRef.value) {
    inputValueRef.value = newValue;
  }
});
</script>
<template>
  <div v-if="displayBtn && !isOpen || !inputValue" class="promo">
    <div
      :onClick="
        (e) => {
          addPromoButtonHandler();
        }
      "
      class="
        promo__btn
        fs-14
        text-KQ-White
        bg-KQ-Grey2
        display--flex
        items-center
        text--bold
        paragraph-note--sm
      "
    >
      <div class="promo__btn__icon display--flex items-center">
        <i
          class="icon icon-plus icon--xxs icon-color--white"
          aria-hidden="true"
        />
      </div>
      {{ cmsProps?.addPromoCodeLabel.value ?? 'Add promo code' }}
    </div>
  </div>
  <div v-if="(isOpen || promoCodeResponse) && !displayBtn" class="display-promo items-center" :class="{'display-promo--active': isOpen && !isMobile}">
    <input
      v-model="inputValueRef"
      type="text"
      class="display-promo__input"
      name="lname"      
      autocomplete="off"      
      :disabled="promoCodeResponse && !isOpen"
      @keyup.enter="promoEnterHandler"
      @blur="blurHandler"
      @focus="focusHandler"
    />
    <div v-if="promoCodeResponse && !isOpen" class="display--flex items-center" @click="resetPromo">
      <i class="icon icon-close icon-color--grey3 icon--xxs"></i>
    </div>
  </div>
  <p v-if="error" class="text-KQ-Brand-Primary">Invalid promo code</p>

  <BaseModal v-if="isMobile" @update:state="childEmits">
    <template #modal-content>
      <div class="base-model__content promo-modal">
        <div class="base-model__header">
          <p
            class="
              base-model__header-title
              fs-16
              text--bold
              paragraph-body
              text-KQ-Grey1
            "
          >
            {{ cmsProps?.addPromoCodeLabel.value ?? 'Add promo code' }}
          </p>
          <button
            role="button"
            aria-label="Dismiss modal"
            class="button--text button--icon base-model__header-close"
            :onClick="
              () => {
                closeModal();
              }
            "
          >
            <i class="icon icon-close icon--xs" aria-hidden="true"></i>
          </button>
        </div>
        <div class="base-model__body">
          <InputField
            ref="child"
            v-model="inputValueRef"
            :placeholder="'Enter promo code'"
            :name="name"
            :error="error"
            hasClear="false"
            @update:inputValue="updateInput"
            @update:hasFocus="focusHandler"
            @update:blurred="blurHandler"
          />
          <p v-if="error" class="text-KQ-Brand-Primary">Invalid promo code</p>
          <div class="base-model__body-btn">
            <div v-if="loading" class="loader"></div>
            <button
              v-else
              role="button"
              :disabled="isDisabled"
              aria-label="Optional description of what happens when the link is clicked"
              class="button button-lg"
              :onClick="
                () => {
                  setPromoCode(inputValue);
                  closeModal();
                }
              "
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>
